import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, FilePenLine, RotateCcw } from 'lucide-react';
import type { CursorPage, ReviewHistoryRow } from '@dru/types';
import { CORRECTION_STATUS_LABEL, EXAM_TYPE_LABEL, FITNESS_LABEL, formatPaise } from '@dru/types';
import {
  Alert, Card, EmptyState, FilterBar, IconAction, Input, LoadMore, RowActions, SearchInput, StatusBadge, TextSkeleton, fitnessTone, type Tone,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { errorMessage, useAuth } from '../../../lib/auth';
import { formatDateTime } from '../../../lib/format';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { NotForThisAccount } from './Review';

/**
 * Every review the signed-in physician has signed, newest first.
 *
 * A signed review leaves the queue — there is nothing left to do on it — and
 * used to leave the physician's reach with it. This is where it lands, and it
 * stays: the clinical record they signed, readable for as long as it exists.
 *
 * The API takes no physician id. Whose history this is comes from the session,
 * so there is nothing on this screen to change to reach somebody else's.
 *
 * Each row is clinical first. What the signature earned sits on its own quiet
 * line beneath, because money is not what a clinical history is about.
 */
export function History() {
  const { permissions } = useAuth();
  if (!holds(permissions, 'review.queue-read')) return <NotForThisAccount />;
  return <HistoryScreen />;
}

type Filters = Record<string, string | undefined>;

const FILTERS = [
  {
    key: 'decision', label: 'Decision', allLabel: 'Any decision',
    options: (['FIT', 'FIT_WITH_RESTRICTION', 'TEMPORARILY_UNFIT', 'UNFIT', 'FURTHER_EVALUATION'] as const)
      .map((d) => ({ value: d, label: FITNESS_LABEL[d] })),
  },
  {
    key: 'certificateStatus', label: 'Certificate', allLabel: 'Any certificate',
    options: [
      { value: 'ACTIVE', label: 'Certificate active' },
      { value: 'SUPERSEDED', label: 'Certificate superseded' },
      { value: 'REVOKED', label: 'Certificate revoked' },
      { value: 'EXPIRED', label: 'Certificate expired' },
      { value: 'NONE', label: 'No certificate' },
    ],
  },
  {
    key: 'reReviewRequired', label: 'Re-review', allLabel: 'Re-review: any',
    options: [
      { value: 'true', label: 'Needs re-review' },
      { value: 'false', label: 'No re-review open' },
    ],
  },
];

function HistoryScreen() {
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState<Filters>({});
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState<ReviewHistoryRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Typing is not a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = new URLSearchParams();
  if (q) params.set('q', q);
  for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const query = params.toString();

  async function load(after: string | null) {
    const p = new URLSearchParams(query);
    p.set('limit', '25');
    if (after) p.set('cursor', after);
    return api.get<CursorPage<ReviewHistoryRow>>(`/physician/history?${p.toString()}`);
  }

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    load(null)
      .then((page) => { if (live) { setRows(page.items); setCursor(page.nextCursor); } })
      .catch((e) => { if (live) setError(errorMessage(e)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function loadMore() {
    if (!cursor) return;
    setMore(true);
    try {
      const page = await load(cursor);
      setRows((r) => [...r, ...page.items]);
      setCursor(page.nextCursor);
    } catch (e) { setError(errorMessage(e)); } finally { setMore(false); }
  }

  const filtered = Boolean(query);

  return (
    <div className="space-y-4">
      <PageHeader
        className="mb-0"
        title="Review history"
        hint="Every review you have signed. A signed review is read-only; if something in one is wrong, open it and request a correction."
      />

      <FilterBar
        filters={FILTERS}
        value={filters}
        onChange={setFilters}
        more={(
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <label className="flex min-w-0 flex-1 items-center gap-1.5 text-caption text-neutral-600 sm:flex-none">
              <span className="shrink-0">Signed from</span>
              <Input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="h-10 min-w-0" />
            </label>
            <label className="flex min-w-0 flex-1 items-center gap-1.5 text-caption text-neutral-600 sm:flex-none">
              <span className="shrink-0">to</span>
              <Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="h-10 min-w-0" />
            </label>
          </div>
        )}
        moreActive={(from ? 1 : 0) + (to ? 1 : 0)}
      >
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Name, code or certificate"
          aria-label="Search your signed reviews"
          className="w-full sm:w-72"
        />
      </FilterBar>

      {error && <Alert tone="danger">{error}</Alert>}

      {loading ? (
        <Card className="p-4"><TextSkeleton lines={6} /></Card>
      ) : rows.length === 0 ? (
        filtered ? (
          <EmptyState
            title="No signed review matches"
            hint="Change or clear the filters to see more of your history."
          />
        ) : (
          <EmptyState
            title="No signed reviews yet"
            hint="Reviews you sign will remain available here as your clinical history."
          />
        )
      ) : (
        <Card>
          <ul className="divide-y divide-neutral-200">
            {rows.map((r) => <HistoryRow key={r.reviewId} r={r} />)}
          </ul>
          <LoadMore hasMore={!!cursor} loading={more} onLoadMore={loadMore} shown={rows.length} className="border-t border-neutral-200" />
        </Card>
      )}
    </div>
  );
}

function HistoryRow({ r }: { r: ReviewHistoryRow }) {
  const nav = useNavigate();
  const exam = EXAM_TYPE_LABEL[r.examinationType] ?? r.examinationType.replace(/_/g, ' ').toLowerCase();
  const where = [r.company, r.plant?.name].filter(Boolean).join(' · ');
  return (
    <li className="px-4 py-3.5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-[1_1_16rem]">
          <div className="text-body font-semibold text-neutral-900">{r.employee.fullName}</div>
          <div className="text-caption font-normal text-neutral-500">
            <span className="font-mono">{r.employee.employeeCode}</span>
            {where ? <> · {where}</> : null}
          </div>
          <div className="mt-0.5 text-small text-neutral-700">{exam}</div>
        </div>
        <div className="flex min-w-0 flex-col items-start gap-1 sm:items-end">
          {r.decision
            ? <StatusBadge tone={fitnessTone(r.decision)}>{FITNESS_LABEL[r.decision]}</StatusBadge>
            : <StatusBadge tone="neutral">No decision</StatusBadge>}
          <span className="text-caption font-normal tabular-nums text-neutral-500">Signed {formatDateTime(r.signedAt)}</span>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {r.certificate ? (
          <span className="inline-flex min-w-0 items-center gap-1.5 text-caption text-neutral-600">
            <span className="text-neutral-500">Certificate</span>
            <span className="truncate font-mono text-neutral-800">{r.certificate.certificateNumber}</span>
            <StatusBadge tone={certificateTone(r.certificate.status)} className="py-0">{capitalise(r.certificate.status)}</StatusBadge>
          </span>
        ) : (
          <span className="text-caption text-neutral-500">No certificate issued</span>
        )}
        {r.openReassessments > 0 && (
          <StatusBadge tone="danger" className="py-0">
            <RotateCcw aria-hidden className="h-3 w-3" /> Re-review required · report amended
          </StatusBadge>
        )}
        {r.correction && r.correction.status !== 'CANCELLED' && r.correction.status !== 'REJECTED' && (
          <StatusBadge tone={r.correction.status === 'COMPLETED' ? 'neutral' : 'pending'} className="py-0">
            <FilePenLine aria-hidden className="h-3 w-3" /> {CORRECTION_STATUS_LABEL[r.correction.status]}
          </StatusBadge>
        )}
        {r.correctionOfOrderId && (
          <StatusBadge tone="info" className="py-0">Correction of an earlier review</StatusBadge>
        )}
      </div>

      {/* Money beside the medicine, and quieter than it. */}
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 border-t border-dashed border-neutral-200 pt-2">
        <span className="text-caption font-normal text-neutral-500">{earningText(r.earning)}</span>
        <RowActions>
          <IconAction label={`View signed review of ${r.employee.fullName}`} icon={Eye} onClick={() => nav(`/physician/review/${r.orderId}`)} />
        </RowActions>
      </div>
    </li>
  );
}

function earningText(e: ReviewHistoryRow['earning']): string {
  const amount = e.amountPaise !== null ? formatPaise(e.amountPaise) : null;
  switch (e.status) {
    case 'PRICED': return `Earning: ${amount} · Awaiting settlement`;
    case 'IN_SETTLEMENT': return `Earning: ${amount} · In settlement`;
    case 'PAID': return `Earning: ${amount} · Paid`;
    case 'NOT_PAYABLE': return 'Correction re-review — not separately payable';
    default: return 'Earning: awaiting pricing';
  }
}

/** A certificate's lifecycle is operational, not a fitness outcome. */
function certificateTone(status: string): Tone {
  return status === 'ACTIVE' ? 'covered' : status === 'REVOKED' ? 'danger' : 'neutral';
}

function capitalise(s: string): string {
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
